import React, { useEffect, useState, useCallback, useRef } from "react";
import { observer } from "mobx-react";
import { useHistory } from "react-router-dom";
import makeApi, { URLS } from "Utils/apiURL";
import { toasterState } from "Components/common/toaster";
import { dataSearchState } from "Pages/Dashboard/dataSearchV2/state";
import SellerIntelSummary from "./SellerIntelSummary";
import SellerIntelligenceEditor from "./SellerIntelligenceEditor";
import {
  mapIndustriesToListBuilder,
  warmIndustriesVocab,
  EMPLOYEE_RANGE_IDS,
  REVENUE_RANGE_IDS
} from "./prospectFilterVocab";

// Status lines map to what the derivation actually produces (truthful, not
// theatrical) — the wait doubles as onboarding for what Seller Intelligence is.
const DERIVE_STATUS_MESSAGES = [
  "🧠 Reading your updated pitch…",
  "👥 Mapping your buyer personas…",
  "⚔️ Placing you against competitors…",
  "✍️ Sharpening your category pitch…"
];

const SENIORITY_TO_LEVEL = {
  founder: "founder", owner: "owner",
  "c-suite": "c_suite", csuite: "c_suite", cxo: "c_suite",
  vp: "vp", "vice president": "vp",
  director: "director", head: "head", manager: "manager",
  senior: "senior", "mid-level": "mid-level", mid: "mid-level", entry: "entry",
};

const buildProspectFilters = async (personas, icp) => {
  if (!Array.isArray(personas) || personas.length === 0) return null;
  const titles = [...new Set(personas.map(p => p.title).filter(Boolean))];
  const levels = [...new Set(personas.map(p => SENIORITY_TO_LEVEL[(p.seniority || "").toLowerCase().trim()]).filter(Boolean))];
  // Industries are validated against the LIVE List Builder vocabulary at
  // click time (normalized matching); anything unmappable is dropped rather
  // than sent as a filter the search would silently mismatch.
  const industries = await mapIndustriesToListBuilder(icp?.industries);
  const employeeCount = (icp?.employee_ranges || []).filter(v => EMPLOYEE_RANGE_IDS.includes(v));
  const revenue = (icp?.revenue_ranges || []).filter(v => REVENUE_RANGE_IDS.includes(v));
  // department/job-function intentionally NOT queried: LLM persona departments
  // don't reliably match the List Builder's job-function vocabulary and were
  // producing wrong matches. Titles + level carry the targeting.
  return {
    title: titles,
    level: levels,
    industry: industries,
    employeeCount,
    revenue
  };
};

// Apply the derived filters to the List Builder store and navigate. Industry
// chips render from selectedFilteredList, so mirror industries there too.
const applyProspectFiltersAndGo = (filters, history) => {
  dataSearchState.applyFiltersFromCompanyExtensionImport(filters);
  if (filters.industry && filters.industry.length) {
    dataSearchState.setSelectedFilteredList({
      industry: filters.industry.map(n => ({ id: n, name: n }))
    });
  }
  history.push("/list-builder");
};

/**
 * Inline version of SellerIntelligenceCard — same render but no modal chrome.
 * Sits as a right-column card on the seller profile edit page so the user
 * sees the AI-derived buyer personas / competitor map / disqualifiers right
 * next to the raw form they're filling in.
 *
 * Auto-loads on mount. Re-derive CTA when profile changes. Edit-as-JSON
 * escape hatch.
 *
 * Pass `triggerDeriveTs` (a timestamp number) from the parent — incrementing
 * it forces a re-derive (used after save).
 */
const SellerIntelligencePanel = observer(({
  sellerProfileId,
  sellerProfileName,
  triggerDeriveTs,
  // Generate once when the profile has no intelligence yet (right after the
  // profile is created). Never re-derives on later saves.
  autoDeriveWhenMissing = false
}) => {
  const history = useHistory();
  const autoDerivedRef = useRef(false);
  const [loading, setLoading] = useState(true);
  const [deriving, setDeriving] = useState(false);
  const [saving, setSaving] = useState(false);
  const [intel, setIntel] = useState(null);
  const [derivedAt, setDerivedAt] = useState(null);
  const [userEdited, setUserEdited] = useState(false);
  const [jsonMode, setJsonMode] = useState(false);
  const [jsonText, setJsonText] = useState("");
  const [jsonErr, setJsonErr] = useState("");
  // Inline structured editor (the friendly editing surface; raw JSON is the
  // "Advanced" escape hatch inside it).
  const [editMode, setEditMode] = useState(false);
  // Collapsed by default (the page's job is the form; the summary answers
  // "who do we target / who do we beat" at a glance) — but the user's manual
  // choice is remembered across visits, and a fresh derive auto-expands once
  // so the payoff isn't hidden behind "Show full details".
  const [expanded, setExpanded] = useState(() => {
    try { return localStorage.getItem("sg-si-panel-expanded") === "1"; } catch (e) { return false; }
  });
  const setExpandedPersist = (val) => {
    setExpanded(val);
    try { localStorage.setItem("sg-si-panel-expanded", val ? "1" : "0"); } catch (e) { /* ignore */ }
  };
  // Derive UX: rotating status line while the LLM works (~7s budget — 4
  // messages at 1.7s, HOLDING the last one; looping back reads as "stuck"),
  // then a one-shot receipts banner with what was produced.
  const [deriveMsgIdx, setDeriveMsgIdx] = useState(0);
  const [justDerived, setJustDerived] = useState(null);

  useEffect(() => {
    if (!deriving) { setDeriveMsgIdx(0); return undefined; }
    const t = setInterval(() => {
      setDeriveMsgIdx((i) => Math.min(i + 1, DERIVE_STATUS_MESSAGES.length - 1));
    }, 1700);
    return () => clearInterval(t);
  }, [deriving]);

  useEffect(() => {
    if (!justDerived) return undefined;
    const t = setTimeout(() => setJustDerived(null), 5000);
    return () => clearTimeout(t);
  }, [justDerived]);

  const load = useCallback(() => {
    if (!sellerProfileId) { setLoading(false); return; }
    setLoading(true);
    makeApi({
      url: `${URLS.deepResearchSellerIntelligence}/${encodeURIComponent(sellerProfileId)}`,
      method: "GET"
    }).then(res => {
      const d = res?.data;
      if (d && d.found) {
        setIntel(d.intelligence || null);
        setDerivedAt(d.derivedAt || null);
        setUserEdited(!!d.userEdited);
      } else {
        setIntel(null);
        setDerivedAt(null);
        if (autoDeriveWhenMissing && !autoDerivedRef.current) {
          autoDerivedRef.current = true;
          derive(true);
        }
      }
      setLoading(false);
    }).catch(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sellerProfileId, autoDeriveWhenMissing]);

  useEffect(() => { load(); }, [load]);

  // Warm the live industry vocabulary so the Find-prospects click is instant.
  useEffect(() => { warmIndustriesVocab(); }, []);

  const derive = useCallback((silent) => {
    if (!sellerProfileId || deriving) return;
    setDeriving(true);
    makeApi({
      url: `${URLS.deepResearchSellerIntelligence}/${encodeURIComponent(sellerProfileId)}/derive`,
      method: "POST",
      data: {}
    }).then(res => {
      const d = res?.data;
      if (d && d.ok && d.intelligence) {
        setIntel(d.intelligence);
        setDerivedAt(d.derivedAt || new Date().toISOString());
        setUserEdited(false);
        // Receipts banner: concrete counts are proof of work done.
        const cm = d.intelligence.competitor_map || {};
        setJustDerived({
          personas: (d.intelligence.canonical_buyer_personas || []).length,
          competitors: (cm.direct || []).length + (cm.adjacent || []).length + (cm.complementary || []).length,
          pains: (d.intelligence.pain_taxonomy || []).length
        });
        // Fresh derive → show the payoff. Contextual auto-expand only; doesn't
        // overwrite the user's persisted collapse preference.
        setExpanded(true);
        if (!silent) toasterState.setToastMsg("Seller intelligence generated", "success");
      } else if (!silent) {
        toasterState.setToastMsg(`Generation failed: ${d?.reason || "unknown"}`, "failure");
      }
      setDeriving(false);
    }).catch(err => {
      if (!silent) toasterState.setToastMsg(`Generation failed: ${err?.message || "unknown"}`, "failure");
      setDeriving(false);
    });
  }, [sellerProfileId, deriving]);

  // External trigger — parent increments triggerDeriveTs after save
  useEffect(() => {
    if (triggerDeriveTs && sellerProfileId) {
      derive(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerDeriveTs]);

  const enterJsonMode = () => {
    setJsonText(JSON.stringify(intel || {}, null, 2));
    setJsonErr("");
    setJsonMode(true);
  };

  // Shared persistence for both editing surfaces (inline editor + raw JSON).
  const persistIntel = (parsed, onDone) => {
    setSaving(true);
    makeApi({
      url: `${URLS.deepResearchSellerIntelligence}/${encodeURIComponent(sellerProfileId)}/update`,
      method: "POST",
      data: { intelligence: parsed }
    }).then(res => {
      const d = res?.data;
      if (d && d.ok) {
        setIntel(parsed);
        setUserEdited(true);
        if (onDone) onDone();
        toasterState.setToastMsg("Saved", "success");
      } else {
        toasterState.setToastMsg(`Save failed: ${d?.reason || "unknown"}`, "failure");
      }
      setSaving(false);
    }).catch(err => {
      toasterState.setToastMsg(`Save failed: ${err?.message || "unknown"}`, "failure");
      setSaving(false);
    });
  };

  const saveJson = () => {
    let parsed;
    try { parsed = JSON.parse(jsonText); }
    catch (e) { setJsonErr(`Invalid JSON: ${e.message}`); return; }
    persistIntel(parsed, () => setJsonMode(false));
  };

  const Section = ({ title, children, count }) => (
    <div className="siPanel__section">
      <div className="siPanel__section-head">
        <h4>{title}</h4>
        {count != null && <span className="siPanel__count">{count}</span>}
      </div>
      {children}
    </div>
  );

  const Chips = ({ items, variant }) => {
    if (!Array.isArray(items) || items.length === 0) return <p className="siPanel__empty">—</p>;
    return (
      <div className="siPanel__chips">
        {items.map((s, i) => <span key={i} className={`siPanel__chip ${variant ? `siPanel__chip--${variant}` : ""}`}>{s}</span>)}
      </div>
    );
  };

  if (!sellerProfileId) {
    return (
      <aside className="siPanel">
        <div className="siPanel__header">
          <h3>🧠 Seller Intelligence</h3>
          <p className="siPanel__sub">Save the profile first — then the AI will generate your buyer personas, competitor map, and account disqualifiers.</p>
        </div>
      </aside>
    );
  }

  return (
    <aside className={`siPanel ${!deriving && justDerived ? "siPanel--pulse" : ""}`}>
      <div className="siPanel__header">
        <div>
          <h3>🧠 Seller Intelligence</h3>
          <p className="siPanel__sub">
            AI-generated from this profile. Used by every account research to make precise buyer-persona, competitor, and disqualifier calls.
            {derivedAt && <span className="siPanel__age"> · generated {new Date(derivedAt).toLocaleString()}</span>}
            {userEdited && <span className="siPanel__edited"> · user-edited</span>}
          </p>
        </div>
        {!editMode && (
        <div className="siPanel__actions">
          {!loading && intel && !jsonMode && (
            <button type="button" className="siPanel__btn ghost" onClick={() => setEditMode(true)}>
              ✏️ Edit
            </button>
          )}
          <button
            type="button"
            className={`siPanel__btn ${intel ? "ghost" : "primary"}`}
            onClick={() => derive(false)}
            disabled={deriving}
          >
            {deriving ? "Generating…" : (intel ? "Regenerate" : "Generate")}
          </button>
          {!loading && intel && !jsonMode &&
            Array.isArray(intel.canonical_buyer_personas) &&
            intel.canonical_buyer_personas.length > 0 && (
            <button
              type="button"
              className="siPanel__btn siPanel__btn--findProspects"
              onClick={async () => {
                const filters = await buildProspectFilters(
                  intel.canonical_buyer_personas,
                  intel.ideal_customer_profile
                );
                if (!filters) return;
                applyProspectFiltersAndGo(filters, history);
              }}
            >
              🔍 Find prospects
            </button>
          )}
        </div>
        )}
      </div>

      <div className={`siPanel__body ${deriving ? "siPanel__body--deriving" : ""}`}>
        {deriving && (
          <div className="siPanel__deriveOverlay">
            <span className="siPanel__deriveSpinner" />
            <span key={deriveMsgIdx} className="siPanel__deriveMsg">
              {DERIVE_STATUS_MESSAGES[deriveMsgIdx]}
            </span>
          </div>
        )}

        {!deriving && justDerived && (
          <div className="siPanel__deriveDone">
            ✨ Intelligence updated — {justDerived.personas} personas · {justDerived.competitors} competitors · {justDerived.pains} pains
          </div>
        )}

        {loading && <div className="siPanel__loading">Loading…</div>}

        {!loading && !intel && !deriving && (
          <div className="siPanel__empty-state">
            <p>No intelligence generated yet. Click <strong>Generate</strong> — takes a few seconds.</p>
          </div>
        )}

        {!loading && intel && editMode && !jsonMode && (
          <SellerIntelligenceEditor
            intel={intel}
            saving={saving}
            onSave={(parsed) => persistIntel(parsed, () => setEditMode(false))}
            onCancel={() => setEditMode(false)}
            onAdvanced={() => { setEditMode(false); enterJsonMode(); }}
          />
        )}

        {!loading && intel && !jsonMode && !editMode && (
          <>
            {intel.category_pitch_one_liner && (
              <div className="siPanel__pitch">
                <span className="siPanel__pitch-label">Category pitch</span>
                <p>{intel.category_pitch_one_liner}</p>
              </div>
            )}

            {/* Condensed summary always visible at top — the 80/20 view */}
            <SellerIntelSummary intel={intel} variant="panel" />

            {!expanded && (
              <button
                type="button"
                className="siPanel__expand"
                onClick={() => setExpandedPersist(true)}
              >
                Show full details ▾
              </button>
            )}

            {expanded && (
              <>
            <Section title="✅ Canonical buyer personas" count={(intel.canonical_buyer_personas || []).length}>
              {(intel.canonical_buyer_personas || []).length === 0 ? <p className="siPanel__empty">—</p> : (
                <ul className="siPanel__personas">
                  {(intel.canonical_buyer_personas || []).map((p, i) => (
                    <li key={i} className="siPanel__persona">
                      <div className="siPanel__persona-head">
                        <strong>{p.title}</strong>
                        {p.seniority && <span className="siPanel__pill">{p.seniority}</span>}
                        {p.meddic_role_typical && <span className="siPanel__pill siPanel__pill--meddic">{p.meddic_role_typical}</span>}
                        {p.department && <span className="siPanel__pill siPanel__pill--dept">{p.department}</span>}
                      </div>
                      {p.why_this_persona && <p className="siPanel__persona-why">{p.why_this_persona}</p>}
                      {Array.isArray(p.title_keywords) && p.title_keywords.length > 0 && (
                        <div className="siPanel__chips siPanel__chips--inline">
                          {p.title_keywords.map((k, j) => <span key={j} className="siPanel__chip siPanel__chip--key">{k}</span>)}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </Section>

            <Section title="🚫 Anti-personas" count={(intel.anti_personas || []).length}>
              {(intel.anti_personas || []).length === 0 ? <p className="siPanel__empty">—</p> : (
                <ul className="siPanel__antis">
                  {(intel.anti_personas || []).map((a, i) => (
                    <li key={i}><strong>{a.title_pattern}</strong>{a.why_not && <span> — {a.why_not}</span>}</li>
                  ))}
                </ul>
              )}
            </Section>

            <Section title="⚔️ Competitor map">
              <div className="siPanel__cmap">
                <div><span className="siPanel__cmap-label siPanel__cmap-label--direct">Direct</span><Chips items={intel.competitor_map?.direct} variant="direct" /></div>
                <div><span className="siPanel__cmap-label siPanel__cmap-label--adj">Adjacent</span><Chips items={intel.competitor_map?.adjacent} variant="adj" /></div>
                <div><span className="siPanel__cmap-label siPanel__cmap-label--comp">Complementary</span><Chips items={intel.competitor_map?.complementary} variant="comp" /></div>
              </div>
            </Section>

            <Section title="📉 Pain taxonomy" count={(intel.pain_taxonomy || []).length}>
              {(intel.pain_taxonomy || []).length === 0 ? <p className="siPanel__empty">—</p> : (
                <ul className="siPanel__pains">
                  {(intel.pain_taxonomy || []).map((p, i) => (
                    <li key={i}>
                      <strong>{p.pain}</strong>
                      {p.evidence_we_solve_it && <p>How we solve: {p.evidence_we_solve_it}</p>}
                      {p.language_to_use && <p className="siPanel__lang">Their words: "<em>{p.language_to_use}</em>"</p>}
                    </li>
                  ))}
                </ul>
              )}
            </Section>

            <Section title="📡 Buying signals" count={(intel.buying_signals_taxonomy || []).length}>
              {(intel.buying_signals_taxonomy || []).length === 0 ? <p className="siPanel__empty">—</p> : (
                <ul className="siPanel__signals">
                  {(intel.buying_signals_taxonomy || []).map((s, i) => (
                    <li key={i}>
                      <strong>{s.signal_kind}</strong>
                      {s.urgency_default && <span className={`siPanel__urg siPanel__urg--${String(s.urgency_default).toLowerCase()}`}>{s.urgency_default}</span>}
                      {s.why_it_triggers_us && <p>{s.why_it_triggers_us}</p>}
                    </li>
                  ))}
                </ul>
              )}
            </Section>

            <Section title="🛑 Account disqualifiers" count={(intel.account_disqualifiers || []).length}>
              <Chips items={intel.account_disqualifiers} />
            </Section>

            <Section title="🎯 Ideal customer profile">
              {(() => {
                const icp = intel.ideal_customer_profile || {};
                const money = n => (n >= 1e9 ? `$${n / 1e9}B` : n >= 1e6 ? `$${n / 1e6}M` : `$${Math.round(n / 1e3)}K`);
                const fmtRevenue = id => {
                  const [a, b] = String(id).split("-").map(Number);
                  if (!a && a !== 0) return id;
                  return b ? `${money(a)} - ${money(b + 1)}` : `${money(a)}+`;
                };
                const founded = icp.founded_year_start || icp.founded_year_end
                  ? `${icp.founded_year_start || "any"} to ${icp.founded_year_end || "present"}`
                  : "";
                const growth = (icp.employee_growth || [])
                  .filter(g => g && g.min_growth_percent)
                  .map(g => `${(g.department || "all").replace("_", " ")} +${g.min_growth_percent}%`);
                const rows = [
                  ["Industries", icp.industries],
                  ["Company size", icp.employee_ranges],
                  ["Revenue", (icp.revenue_ranges || []).map(fmtRevenue)],
                  ["HQ locations", icp.hq_locations],
                  ["Company types", icp.company_types],
                  ["Founded", founded ? [founded] : []],
                  ["Technologies", icp.technologies],
                  ["Keywords to include", icp.keywords_include],
                  ["Keywords to exclude", icp.keywords_exclude],
                  ["Funding stages", icp.funding_stages],
                  ["Headcount growth", growth]
                ].filter(([, v]) => Array.isArray(v) && v.length);
                return rows.length === 0 ? <p className="siPanel__empty">—</p> : (
                  <div className="siPanel__icpGrid">
                    {rows.map(([label, values]) => (
                      <div className="siPanel__icpRow" key={label}>
                        <span className="siPanel__icpLabel">{label}</span>
                        <Chips items={values} />
                      </div>
                    ))}
                  </div>
                );
              })()}
            </Section>

            <Section title="🏆 Proof points" count={(intel.proof_points || []).length}>
              {(intel.proof_points || []).length === 0 ? <p className="siPanel__empty">—</p> : (
                <ul className="siPanel__proof">
                  {(intel.proof_points || []).map((p, i) => (
                    <li key={i}>
                      <strong>{p.customer_segment}</strong>
                      {p.outcome_metric && <span> — {p.outcome_metric}</span>}
                      {p.source_hint && <span className="siPanel__src"> ({p.source_hint})</span>}
                    </li>
                  ))}
                </ul>
              )}
            </Section>

                <button
                  type="button"
                  className="siPanel__expand"
                  onClick={() => setExpandedPersist(false)}
                >
                  Show less ▴
                </button>
              </>
            )}
          </>
        )}

        {!loading && intel && jsonMode && (
          <div className="siPanel__json">
            <textarea
              className="siPanel__json-area"
              value={jsonText}
              onChange={(e) => { setJsonText(e.target.value); setJsonErr(""); }}
              spellCheck={false}
            />
            {jsonErr && <p className="siPanel__json-err">{jsonErr}</p>}
            <div className="siPanel__json-actions">
              <button type="button" className="siPanel__btn ghost" onClick={() => setJsonMode(false)}>
                Cancel
              </button>
              <button type="button" className="siPanel__btn primary" onClick={saveJson} disabled={saving}>
                {saving ? "Saving…" : "Save edits"}
              </button>
            </div>
          </div>
        )}
      </div>
    </aside>
  );
});

export default SellerIntelligencePanel;
